import React from 'react';
import { has, isEmpty, isNil, map } from 'lodash';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { TopAttribute } from 'src/services/configuration/codecs/viewdefns/general';
import Renderer from 'src/utils/Domain/Renderer';
import styles from 'src/components/ConfigurableGrid/Macros/Macros.styles';
import { evaluateFormula } from 'src/utils/LibraryUtils/MathUtils';
import * as globalMath from 'mathjs';
import { all } from 'mathjs';
import { inputIsNotNullOrUndefined } from 'src/utils/Functions/epicsFunctions';

const math = globalMath.create(all) as globalMath.MathJsStatic;
math.import(
  {
    nil: (v: unknown) => v == null,
  },
  { silent: true },
);

export function generateRollupText(data: BasicPivotItem | undefined, configuratorViewDefn: any) {
  if (isNil(data) || isNil(configuratorViewDefn)) {
    return [];
  }
  let rollupText: string[] = [];
  if (configuratorViewDefn.topAttributes) {
    rollupText = configuratorViewDefn.topAttributes.left.map((config: TopAttribute) => {
      const mask = config.mask;
      const renderer = Renderer[config.renderer];
      let staticClasses = config.classes;
      let maybeEvaluatedClasses: string[] = [];
      // We do this to prevent screen from crashing when fields are missing from data
      try {
        // This branch is for dynamic rendering of cssClasses (possibly in addition the static type)
        // If `rendererParams.calcClasses` is present, then we go through this branch
        // and build up the evaluated classes
        if (has(config, 'rendererParams.classByCalc')) {
          const calcClasses = config.rendererParams?.classByCalc;
          maybeEvaluatedClasses = map(calcClasses, (exp, cssClass) => {
            return !!math.evaluate(exp, data) ? cssClass : undefined;
          }).filter(inputIsNotNullOrUndefined);

          // if there are no staticClasses, just replace them with the evaluated ones
          // this is so we make the minimal change to the `value` ternary below
          if (maybeEvaluatedClasses && !staticClasses) {
            staticClasses = maybeEvaluatedClasses;
          }
        }
        const value =
          renderer && mask && staticClasses
            ? Renderer[config.renderer](data, mask, staticClasses.concat(maybeEvaluatedClasses))
            : renderer && mask
              ? Renderer[config.renderer](data, mask)
              : '';
        return value;
      } catch (e) {
        console.warn(`Error building template for left text. \n ${e}`); // eslint-disable-line no-console
        return '';
      }
    });
  }

  return rollupText;
}

interface RollupProps {
  config: unknown;
  topAttributes: BasicPivotItem | undefined;
}

export const Rollup = ({ config, topAttributes }: RollupProps) => {
  return (
    <div className={styles.rollupsContainer}>
      {generateRollupText(topAttributes, config).map((text: string) => {
        return <span key={text}>{text}</span>;
      })}
    </div>
  );
};
